import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Bucket,
  Granularity,
  MAX_VISIBLE_KEYS,
  Metric,
  OTHER_COLOR,
  OTHER_KEY,
  OTHER_KEY_ID,
  SERIES_COLORS,
  KindUsageRow,
  TokenKind,
  bucketize,
  filterRows,
  metricValue,
  rankKeys,
} from './usageBreakdown';
import { CHART_SURFACE, GRID_STROKE, X_AXIS_PROPS, Y_AXIS_PROPS, formatCompact, ChartTooltip, toggleClass } from './chartKit';
import { DateRange } from '../filters/dateRange';
import { urlEnum, useUrlState } from '../../hooks/useUrlState';

interface SeriesDef {
  dataKey: string;
  label: string;
  color: string;
  total: number;
}

// Presentational: the API key filter and the date range live in the page-level filter bar.
// The server sends its top keys by both metrics and folds the rest into "Other".
// bucketize folds whatever this metric does not show into the same bucket, so the toggle needs no refetch.
// A null range means the custom range is incomplete.
// metric selects a token kind or count; countLabel adds the count button.
// Defaults omit metric from the URL; urlPrefix separates charts on one page.
type ChartMetric = 'converted-in-tok' | 'supported-in-tok' | 'in-tok' | 'out-tok' | 'op';
const kindMetrics = { converted: 'converted-in-tok', supported: 'supported-in-tok', input: 'in-tok', output: 'out-tok' } as const;
// Short names for the buttons, full names for the total label.
const kindLabels = { input: 'In Tokens', converted: 'In Tokens (Converted)', supported: 'In Tokens (Supported)', output: 'Out Tokens' };
const kindTotalLabels = { input: 'Total input tokens', converted: 'Total converted input tokens', supported: 'Total supported input tokens', output: 'Total output tokens' };
const CHART_URL_SPEC = {
  gran: urlEnum<Granularity>(['day', 'week'], 'day'),
  metric: urlEnum<ChartMetric>(['converted-in-tok', 'supported-in-tok', 'in-tok', 'out-tok', 'op'], 'converted-in-tok'),
};

interface ChartProps {
  rows: KindUsageRow[];
  tokenKinds: TokenKind[];
  range: DateRange | null;
  countLabel?: string;
  error?: boolean;
  urlPrefix?: string;
}

function UsageBreakdownChart({ rows, range, countLabel, error, urlPrefix, tokenKinds }: ChartProps) {
  const [{ gran: granularity, metric: urlMetric }, setChartState] = useUrlState(CHART_URL_SPEC, urlPrefix);
  const setGranularity = (gran: Granularity) => setChartState({ gran });
  const setMetric = (next: ChartMetric) => setChartState({ metric: next });
  // The spec already limits the value; this drops what this page does not offer.
  const offered = urlMetric === 'op' ? !!countLabel : tokenKinds.some((choice) => kindMetrics[choice] === urlMetric);
  const selectedMetric = offered ? urlMetric : CHART_URL_SPEC.metric.default;
  const metric: Metric = selectedMetric === 'op' ? 'count' : 'tokens';
  const kind = tokenKinds.find((choice) => kindMetrics[choice] === selectedMetric) || 'converted';
  const valuedRows = useMemo(
    () => rows.map((row) => ({ ...row, tokens: row.tokensByKind[kind] })),
    [rows, kind],
  );

  const rangeRows = useMemo(() => range ? filterRows(valuedRows, range) : [], [valuedRows, range]);

  // Ranks use the metric on screen and rows inside the range.
  // Granularity keeps colors stable. The metric toggle can change ranks and colors.
  const allKeys = useMemo(() => rankKeys(rangeRows, metric), [rangeRows, metric]);
  const colorByKey = useMemo(() => {
    const map = new Map<number, string>();
    allKeys.slice(0, MAX_VISIBLE_KEYS).forEach((k, i) => map.set(k.keyId, SERIES_COLORS[i]));
    return map;
  }, [allKeys]);

  const { buckets, series, periodTotal } = useMemo(() => {
    if (!range) return { buckets: [] as Bucket[], series: [] as SeriesDef[], periodTotal: 0 };

    const visibleKeys = allKeys.slice(0, MAX_VISIBLE_KEYS);
    const buckets = bucketize(rangeRows, range, granularity, visibleKeys.map((k) => k.keyId), metric);
    const periodTotal = rangeRows.reduce((sum, r) => sum + metricValue(r, metric), 0);

    const series: SeriesDef[] = visibleKeys
      .map((k) => ({
        dataKey: `k${k.keyId}`,
        total: metric === 'count' ? k.count : k.tokens,
        label: k.keyLabel,
        color: colorByKey.get(k.keyId) || OTHER_COLOR,
      }))
      // Recharts draws the first bar at the bottom, so reverse rank order:
      // the legend then reads top of the stack first.
      .reverse();
    const hasOther =
      allKeys.length > MAX_VISIBLE_KEYS || rangeRows.some((r) => r.keyId === OTHER_KEY_ID);
    if (hasOther) {
      const visibleTotal = series.reduce((sum, s) => sum + s.total, 0);
      series.unshift({ dataKey: OTHER_KEY, label: 'Other', color: OTHER_COLOR, total: periodTotal - visibleTotal });
    }
    // A key can have operations but no tokens. Drawn as a series it is an
    // invisible bar with a legend entry, so drop it for this metric. Colors come
    // from colorByKey, so a key keeps its color when the other metric brings it back.
    const drawn = series.filter((s) => s.total > 0);
    return { buckets, series: drawn, periodTotal };
  }, [rangeRows, range, granularity, metric, allKeys, colorByKey]);

  const totalLabel = metric === 'tokens' ? kindTotalLabels[kind] : `Total ${countLabel!.toLowerCase()}`;

  return (
    <div className="bg-widget-bg rounded-lg border border-gray-700 p-4 mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div role="group" aria-label="Bucket size" className="flex items-center bg-body-bg rounded-md p-0.5">
          <button aria-pressed={granularity === 'day'} onClick={() => setGranularity('day')} className={toggleClass(granularity === 'day')}>
            Day
          </button>
          <button aria-pressed={granularity === 'week'} onClick={() => setGranularity('week')} className={toggleClass(granularity === 'week')}>
            Week
          </button>
        </div>
        {/* Wraps on narrow screens: up to five metrics do not fit one row. */}
        <div role="group" aria-label="Chart metric" className="flex flex-wrap items-center bg-body-bg rounded-md p-0.5">
          {tokenKinds.map((choice) => (
            <button key={choice} aria-pressed={selectedMetric === kindMetrics[choice]} onClick={() => setMetric(kindMetrics[choice])} className={toggleClass(selectedMetric === kindMetrics[choice])}>
              {kindLabels[choice]}
            </button>
          ))}
          {countLabel && (
            <button aria-pressed={selectedMetric === 'op'} onClick={() => setMetric('op')} className={toggleClass(selectedMetric === 'op')}>
              {countLabel}
            </button>
          )}
        </div>
        <div className="ml-auto text-sm text-secondary">
          {/* A total of 0 next to the error would read as "no usage". */}
          {totalLabel}: <span className="text-heading font-medium">{error ? '—' : periodTotal.toLocaleString()}</span>
        </div>
      </div>

      {!range ? (
        <p className="text-sm text-secondary py-8 text-center">Pick a valid date range.</p>
      ) : error ? (
        <p className="text-sm text-red-400 py-8 text-center">Failed to load the chart.</p>
      ) : periodTotal === 0 ? (
        <p className="text-sm text-secondary py-8 text-center">No usage in this period.</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            {/* Recharts stacks in mount order, so the chart remounts when the series order changes. */}
            <BarChart key={series.map((s) => s.dataKey).join()} data={buckets} barCategoryGap="25%">
              <CartesianGrid vertical={false} stroke={GRID_STROKE} />
              <XAxis dataKey="label" {...X_AXIS_PROPS} />
              <YAxis {...Y_AXIS_PROPS} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              {series.map((s) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.label}
                  stackId="usage"
                  fill={s.color}
                  stroke={CHART_SURFACE}
                  strokeWidth={1}
                  maxBarSize={28}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 px-1">
            {[...series].reverse().map((s) => (
              <span key={s.dataKey} className="flex items-center gap-1.5 text-xs text-body-color">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label} ({formatCompact(s.total)})
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default UsageBreakdownChart;
